// AuthContext — global auth state + API helpers.
// Soporta cookie httpOnly (preferido) y fallback Bearer token en localStorage
// (necesario en Safari iOS por ITP, que borra cookies de terceros).
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "session_token";

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function clearStoredToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

function authHeaders() {
  const tok = getStoredToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/auth/me`, {
        credentials: "include",
        headers: { ...authHeaders() },
      });
      if (!r.ok) throw new Error("not authed");
      const u = await r.json();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me check if returning from OAuth callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders() },
      });
    } catch {}
    clearStoredToken();
    setUser(null);
  };

  // Login: redirect to Emergent OAuth
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + "/viajes";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// API helper that always includes credentials + Bearer token fallback
export async function api(path, options = {}) {
  const r = await fetch(`${API}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`API ${r.status}: ${text}`);
  }
  return r.json();
}
